<template>
  <div v-if="showAd" class="ad-overlay">
    <!-- 关闭按钮 -->
    <button class="ad-close-btn" @click="closeAd">
      <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
        <path d="M18.3 5.71c-.39-.39-1.02-.39-1.41 0L12 10.59 7.11 5.7c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.88c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z"/>
      </svg>
    </button>

    <!-- 加载状态 -->
    <div v-if="!adContainerReady" class="ad-loading">
      <div class="loading-spinner"></div>
      <div class="loading-text">广告加载中...</div>
    </div>

    <!-- 广告容器 -->
    <div v-if="adContainerReady" class="ad-container">
      <!-- 轮播图容器 -->
      <div 
        class="ad-carousel" 
        @mouseenter="showNavButtons = true" 
        @mouseleave="showNavButtons = false"
      >
        <div 
          class="ad-slides" 
          ref="adSlidesRef"
          :style="{
            transition: isTransitioning ? 'transform 0.5s ease' : 'none',
            transform: `translateX(-${currentAdIndex * 100}%)`
          }"
        >
          <div
            v-for="(image, index) in adImages"
            :key="`slide-${index}`"
            class="ad-slide"
            @click="handleAdClick(image)"
          >
            <img 
              :src="image.src" 
              :alt="image.alt"
              draggable="false"
            />
          </div>
        </div>

        <!-- 左右切换按钮 -->
        <div v-show="showNavButtons && originalAdImages.length > 1" class="ad-nav-buttons">
          <!-- 左切换按钮 -->
          <button class="ad-nav-btn ad-nav-prev" @click="prevAd">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>

          <!-- 右切换按钮 -->
          <button class="ad-nav-btn ad-nav-next" @click="nextAd">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useAdService } from '@/composables/useAdService'

// Props
const props = defineProps({
  autoPlay: {
    type: Boolean,
    default: true
  },
  interval: {
    type: Number,
    default: 3000
  },
  showCloseButton: {
    type: Boolean,
    default: true
  }
})

// Emits
const emit = defineEmits(['close', 'adClick', 'error'])

// 使用广告服务
const { fetchAds, recordAdClick } = useAdService()

// 响应式状态
const showAd = ref(true)
const adContainerReady = ref(false)
const currentAdIndex = ref(1)
const showNavButtons = ref(false)
const isTransitioning = ref(true)
const isAnimating = ref(false)

// 加载状态

// 广告数据
const adImages = ref([])
const originalAdImages = ref([])

// DOM引用
const adSlidesRef = ref(null)

// 定时器
let adTimer = null
let loadingTimer = null

// 创建无缝轮播数组
const createSeamlessAdImages = (images) => {
  if (images.length === 0) return []
  if (images.length === 1) return images
  return [
    images[images.length - 1], // 克隆最后一张
    ...images,                  // 原始图片
    images[0]                   // 克隆第一张
  ]
}

// 预加载图片
const preloadImages = (imageUrls, timeout = 8000) => {
  if (!imageUrls || imageUrls.length === 0) {
    return Promise.resolve([])
  }

  return Promise.all(
    imageUrls.map((imageData) =>
      new Promise((resolve) => {
        const img = new Image()
        let resolved = false
        
        const handleResolve = (result) => {
          if (!resolved) {
            resolved = true
            resolve(result)
          }
        }
        
        img.onload = () => handleResolve(imageData)
        img.onerror = () => handleResolve(null)
        
        // 超时处理
        setTimeout(() => {
          if (!resolved) {
            handleResolve(null)
          }
        }, timeout)
        
        img.src = imageData.src
      })
    )
  ).then((results) => results.filter(Boolean))
}

// 启动自动播放定时器
const startAdTimer = () => {
  if (originalAdImages.value.length <= 1 || !props.autoPlay) return
  stopAdTimer()
  adTimer = setInterval(() => {
    if (!isAnimating.value) {
      nextAd()
    }
  }, props.interval)
}

// 停止自动播放定时器
const stopAdTimer = () => {
  if (adTimer) {
    clearInterval(adTimer)
    adTimer = null
  }
}

// 清理函数
const cleanup = () => {
  stopAdTimer()
  
  // 清理加载超时定时器
  if (loadingTimer) {
    clearTimeout(loadingTimer)
    loadingTimer = null
  }
  
  if (adSlidesRef.value) {
    adSlidesRef.value.removeEventListener('transitionend', handleTransitionEnd)
  }
}

// 关闭广告
const closeAd = () => {
  showAd.value = false
  adContainerReady.value = false
  cleanup()
  emit('close')
}


// 处理广告点击
const handleAdClick = async (image) => {
  try {
    // 记录点击
    await recordAdClick(image.id)
    
    // 打开链接
    if (image.link) {
      window.open(image.link, '_blank')
    }
    
    emit('adClick', image)
  } catch (error) {
    // 静默处理错误
  }
}

// 上一张广告
const prevAd = () => {
  if (originalAdImages.value.length <= 1 || isAnimating.value) return

  isAnimating.value = true
  stopAdTimer()
  isTransitioning.value = true
  currentAdIndex.value--
  startAdTimer()
}

// 下一张广告
const nextAd = () => {
  if (originalAdImages.value.length <= 1 || isAnimating.value) return
  

  isAnimating.value = true
  stopAdTimer()
  isTransitioning.value = true
  currentAdIndex.value++
  startAdTimer()
}


// 处理过渡结束事件
const handleTransitionEnd = (e) => {
  // 只处理 transform 结束事件
  if (e.propertyName !== 'transform') return
  
  isAnimating.value = false // 允许下一次点击
  
  const total = originalAdImages.value.length
  if (total <= 1) return

  const resetIfNeeded = (targetIndex) => {
    // 关闭过渡，瞬间跳转
    isTransitioning.value = false
    currentAdIndex.value = targetIndex
    // 两帧之后再打开过渡，避免闪烁
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isTransitioning.value = true
      })
    })
  }
  
  // 如果移动到了最前面的克隆（索引 0）
  if (currentAdIndex.value === 0) {
    resetIfNeeded(total)
  }
  
  // 如果移动到了最后面的克隆（索引 total + 1）
  if (currentAdIndex.value === total + 1) {
    resetIfNeeded(1)
  }
}

// 设置随机起始广告
const setRandomStartAd = () => {
  if (adImages.value.length > 0) {
    const randomIndex = Math.floor(Math.random() * originalAdImages.value.length)
    currentAdIndex.value = randomIndex + 1 // +1 因为第0项是复制项
  }
}

// 初始化广告系统
const initializeAdSystem = async () => {
  try {
    // 设置整体超时定时器（10秒）
    loadingTimer = setTimeout(() => {
      if (!adContainerReady.value) {
        closeAd() // 超时直接关闭
      }
    }, 10000)
    
    const data = await fetchAds()
    
    if (!data || data.length === 0) {
      showAd.value = false
      return
    }
    
    const adData = data.map(item => ({
      id: item.id,
      src: item.url,
      alt: item.alt,
      link: item.link
    }))
    
    // 预加载所有广告图片
    const preloadedImages = await preloadImages(adData, 8000)
    
    if (preloadedImages.length === 0) {
      closeAd() // 没有可用图片直接关闭
      return
    }
    
    // 设置广告数据
    originalAdImages.value = preloadedImages
    adImages.value = createSeamlessAdImages(preloadedImages)
    
    // 设置随机起始广告
    if (preloadedImages.length > 0) {
      setRandomStartAd()
    }
    
    // 清理加载定时器
    if (loadingTimer) {
      clearTimeout(loadingTimer)
      loadingTimer = null
    }
    
    // 标记广告容器准备就绪
    adContainerReady.value = true
    
    // 启动轮播定时器
    if (preloadedImages.length > 1) {
      setTimeout(() => {
        startAdTimer()
      }, 100)
    }
    
  } catch (error) {
    closeAd() // 发生错误直接关闭
  }
}

// 生命周期
onMounted(async () => {
  await initializeAdSystem()
  
  // 注册 transitionend 监听，用于无缝衔接
  if (adSlidesRef.value) {
    adSlidesRef.value.addEventListener('transitionend', handleTransitionEnd)
  }
})

onUnmounted(() => {
  cleanup()
})

// 暴露方法给父组件
defineExpose({
  nextAd,
  prevAd,
  closeAd
})
</script>

<style scoped>
/* 广告遮罩层 */
.ad-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.ad-container {
  position: relative;
  max-width: 450px;
  max-height: 90vh;
  width: 60%;
  aspect-ratio: 3/4;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.ad-close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
}

.ad-close-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.1);
}

/* 加载状态 */
.ad-loading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: #fff;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #409EFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: #fff;
  margin-bottom: 8px;
}


/* 轮播容器 */
.ad-carousel {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.ad-slides {
  display: flex;
  width: 100%;
  height: 100%;
}

.ad-slide {
  flex: 0 0 100%;
  min-width: 100%;
  height: 100%;
  cursor: pointer;
}

.ad-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.ad-slide:hover img {
  transform: scale(1.02);
}

/* 导航按钮 */
.ad-nav-buttons {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.ad-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: auto;
  opacity: 0.8;
  backdrop-filter: blur(4px);
}

.ad-nav-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  opacity: 1;
  transform: translateY(-50%) scale(1.1);
}

.ad-nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ad-nav-prev {
  left: 15px;
}

.ad-nav-next {
  right: 15px;
}

.ad-nav-btn svg {
  transition: transform 0.3s ease;
}

.ad-nav-btn:hover svg {
  transform: scale(1.2);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .ad-container {
    width: 45%;
    max-height: 90vh;
  }
  
  .ad-close-btn {
    width: 36px;
    height: 36px;
    top: 10px;
    right: 10px;
  }
  
  .ad-nav-btn {
    width: 40px;
    height: 40px;
  }
  
  .ad-nav-prev {
    left: 10px;
  }
  
  .ad-nav-next {
    right: 10px;
  }
}

@media (max-width: 480px) {
  .ad-container {
    width: 80%;
    max-height: 90vh;
  }
  
  .ad-nav-btn {
    width: 36px;
    height: 36px;
  }
}

/* 触摸设备优化 */
@media (hover: none) and (pointer: coarse) {
  .ad-nav-btn {
    opacity: 1;
  }
  
  .ad-close-btn {
    width: 44px;
    height: 44px;
  }
}
</style>